import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, FieldArray } from 'formik';
import * as yup from 'yup';
import {
  Button,
  Grid,
  Switch,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icon,
  IconButton,
  CircularProgress,
  MenuItem,
  CardContent,
  Typography,
  Divider
} from '@material-ui/core';
import MUIDataTable from 'mui-datatables';
import NumberFormat from 'react-number-format';
import { ConfirmDialog } from 'components';
import {
  editUserStoreProduct,
  cancelAddSubscriptionOption,
  editSubscriptionOption
} from 'redux/actions/products';
import {
  addSubscriptionOptionApi,
  deleteSubscriptionOptionApi,
  editSubscriptionOptionApi,
  deleteOneTimeSubscriptionOptionApi,
  saveOneTimePurchaseProductAttrApi,
  saveStoreProductSubscriptionAttrApi,
  addTasksToQueueApi
} from 'api';
import { makeStyles } from '@material-ui/core/styles';
import round from 'helpers/mathRound';


const useStyles = makeStyles(() => ({
  customInput: {
    '& > div.MuiInputBase-root': {
      '& .MuiOutlinedInput-input.MuiInputBase-input': {
        padding: '6px'
      }
    }
  },
  dialog: {
    '& > .MuiDialog-container .MuiDialog-paper': {
      width: '100%'
    },
    '& .MuiAutocomplete-inputRoot[class*=\'MuiOutlinedInput-root\']': {
      padding: '6px'
    }
  }
}));

const freequencyOptions = ['1 month', '2 months', '3 months', '4 months', '6 months', '1 year'];

const Subscription = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [openAddingSubscriptionsDialog, setOpenAddingSubscriptionsDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [editableOptionId, setEditableOptionId] = useState(null);
  const [updateTitle, setUpdatedTitle] = useState(false);

  const handleCloseAddingSubscriptionsDialog = () => {
    dispatch(cancelAddSubscriptionOption());
    setOpenAddingSubscriptionsDialog(false);
  };

  const {
    editable_store_product,
    editable_store_product: {id, sku},
    editable_store_product_no_changes,
    newOption,
    subscriptionOptions,
    editSubscriptionOptionLoading,
    addSubscriptionOptionLoading,
    deleteOneTimeSubscriptionOptionLoading,
    savingProductSubscriptionAttr,
  } = useSelector((state) => state.products);

  const editableSubscirption = subscriptionOptions.find((a) => a.id === editableOptionId);
  const existFrequency = !subscriptionOptions.length ?
    []
    : subscriptionOptions.map((o) => `${o.frequency_count} ${o.frequency_unit}${o.frequency_count > 1 ? 's' : ''}`);
  const isOneTimeEnable = editable_store_product.custom_attributes?.find((a) => a.attribute_code === 'subscription_allow_onetime')?.value == 1;
  const initialSubscriptionLabel = editable_store_product.custom_attributes?.find((attr) => attr.attribute_code === 'subscription_label')?.value || '';
  const [subscriptionLabel, setSubscriptionLabel] = useState(initialSubscriptionLabel || '');

  const isDisableWhileSaving = editSubscriptionOptionLoading ||
    addSubscriptionOptionLoading ||
    deleteOneTimeSubscriptionOptionLoading ||
    savingProductSubscriptionAttr;

  const emptySubscription = {
    product_id: id,
    frequency_count: '',
    frequency_unit: '',
    sproduct_label: '',
    installment_price: '',
    additional_information: '',
    ssort_order: ''
  };

  const columns = [
    {
      name: 'frequency',
      label: 'Frequency',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const {frequency_unit, frequency_count} = subscriptionOptions[dataIndex];

          return (
            <div className="flex items-center">
              {frequency_count != 0 ?
                `Every${frequency_count == 1 ? '' : (` ${frequency_count}`)} ${frequency_unit}${frequency_count > 1 ? 's' : ''}`
                : 'One Time Purchase'
              }
            </div>
          );
        },
      }
    },
    {
      name: 'regular_price',
      label: 'Regular Price',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: () => {
          const priceInUsFormat = Number(editable_store_product.price).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
          return (
            <div className="flex items-center">
              ${priceInUsFormat}
            </div>
          );
        },
      }
    },
    {
      name: 'discount',
      label: 'Discount',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const {frequency_count, additional_information} = subscriptionOptions[dataIndex];
          const discount_percentage = !additional_information?.length
            ? null
            : JSON.parse(`${additional_information[0]}`).discount_percentage;

          return (
            <div className="flex items-center">
              {discount_percentage && Number(discount_percentage)}{frequency_count != 0 ? '%' : ''}
            </div>
          );
        },
      }
    },
    {
      name: 'subscription_price',
      label: 'Subscription Price',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const {additional_information} = subscriptionOptions[dataIndex];
          const discount_percentage = !additional_information?.length
            ? null
            : JSON.parse(`${additional_information[0]}`).discount_percentage;

          const price = discount_percentage ?
            (Number(editable_store_product.price) * (100 - Number(discount_percentage))) / 100
            :
            Number(editable_store_product.price);

          return (
            <div className="flex items-center">
              <NumberFormat
                value={round(price)}
                displayType="text"
                thousandSeparator
                prefix="$"
              />
            </div>
          );
        },
      }
    },
    {
      name: 'ssort_order',
      options: {
        display: false,
        viewColumns: false
      }
    },
    {
      name: 'action',
      label: 'Actions',
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const item = subscriptionOptions[dataIndex];
          const {frequency_count} = item;

          return (
            <>
              {frequency_count != 0 &&
                <div className="flex items-left">
                  {editSubscriptionOptionLoading
                    ? <CircularProgress />
                    :
                    <>
                      <IconButton className="mr-2" title="Delete" onClick={() => deleteSubscriptionOption(item.id)}>
                        <Icon className="text-muted">delete</Icon>
                      </IconButton>
                      <IconButton
                        className="mr-2"
                        title="Edit"
                        onClick={() => {
                          setEditableOptionId(item.id);
                          setOpenEditDialog(item.id);
                        }}
                      >
                        <Icon className="text-gray">edit</Icon>
                      </IconButton>
                    </>
                  }
                </div>
              }
            </>
          );
        },
      }
    }
  ];

  const createOneTimePurchaseOption = async () => {
    const option = {
      product_id: id,
      frequency_count: 0,
      frequency_unit: null,
      length: null,
      installment_price: null,
      adjustment_price: null,
      sproduct_label: null,
      ssort_order: 0
    };
    const product = {
      sku,
      custom_attributes: [{
        attribute_code: 'subscription_allow_onetime',
        value: '1'
      }]
    };
    await addSubscriptionOptionApi({dispatch, subscriptions: [option]});
    await saveOneTimePurchaseProductAttrApi({dispatch, product, sku});
  };

  const deleteOneTimePurchaseOption = async () => {
    const id = subscriptionOptions?.find((option) => option.frequency_count == 0)?.id;

    if (id) {
      await deleteOneTimeSubscriptionOptionApi({dispatch, id});
    }

    const product = {
      sku,
      custom_attributes: [{
        attribute_code: 'subscription_allow_onetime',
        value: '0'
      }]
    };
    await saveOneTimePurchaseProductAttrApi({dispatch, product, sku});
  };

  const handleAllowOneTimePurchase = async (event) => {
    const {name, checked, type, value} = event.target;
    const updatedProduct = {
      custom_attributes: editable_store_product.custom_attributes.map((a) => {
        if (a.attribute_code === name && type === 'checkbox' && name !== 'subscription_allow_onetime') {
          return {...a, value: checked ? '1' : '0'};
        }

        if (a.attribute_code === name && type === 'checkbox' && name === 'subscription_allow_onetime') {
          checked ? createOneTimePurchaseOption() : deleteOneTimePurchaseOption();
          return {...a, value: checked ? '1' : '0'};
        }

        if (a.attribute_code === name && type !== 'checkbox') {
          return {...a, value};
        }

        return a;
      })
    };

    dispatch(editUserStoreProduct(updatedProduct));
    await saveStoreProductSubscriptionAttrApi({
      dispatch,
      product: {
        ...editable_store_product,
        ...updatedProduct,
      },
      sku: editable_store_product.sku,
      attributeCode: 'subscription_active'
    });
  };

  const handleSaveSubscriptionOption = async (values) => {
    setOpenAddingSubscriptionsDialog(false);
    setOpenEditDialog(null);
    const {subscriptions} = values;

    const isSubscriptionEnable = editable_store_product.custom_attributes?.find((a) => a.attribute_code === 'subscription_active')?.value == 1;
    const isPriceChanged = editable_store_product.price !== editable_store_product_no_changes.price;

    if (isPriceChanged) {
      const task = {
        body: editable_store_product,
        name: editable_store_product.name,
        sku: editable_store_product.sku,
        requestType: 'UPDATE_PRODUCT'
      };

      await addTasksToQueueApi({dispatch, tasks: [task]});
    }

    if (editableOptionId === null) {
      await addSubscriptionOptionApi({dispatch, subscriptions});
    } else {
      const isEditedSubscription = await editSubscriptionOptionApi({dispatch, subscriptionOptions: [newOption]});

      if (isEditedSubscription) {
        setEditableOptionId(null);
      }
    }

    if (!isSubscriptionEnable) {
      const updatedProduct = {
        custom_attributes: editable_store_product.custom_attributes.map((a) => {
          if (a.attribute_code === 'subscription_active') {
            return {...a, value: 1};
          }

          return a;
        })
      };
      await saveStoreProductSubscriptionAttrApi({
        dispatch,
        product: {
          ...editable_store_product,
          ...updatedProduct
        },
        sku: editable_store_product.sku,
        attributeCode: 'subscription_active'
      });
    }
  };

  const handleEnableSubscription = (event) => {
    const isSubscriptionEnable = editable_store_product.custom_attributes?.find((a) => a.attribute_code === 'subscription_active')?.value == 1;
    const isOneTimeEnabled = editable_store_product.custom_attributes?.find((a) => a.attribute_code === 'subscription_allow_onetime')?.value == 1;

    const isNoOptions = subscriptionOptions.length === 0 && !isSubscriptionEnable;
    const isOneTimeEnableWithoutOptions = subscriptionOptions.length === 1 && isOneTimeEnabled && !isSubscriptionEnable;

    if (isOneTimeEnableWithoutOptions || isNoOptions) {
      setOpenConfirmDialog(true);
    } else {
      handleAllowOneTimePurchase(event);
    }
  };

  const deleteSubscriptionOption = async (id) => {
    const isOneTimeEnable = editable_store_product.custom_attributes?.find((a) => a.attribute_code === 'subscription_allow_onetime')?.value == 1;
    await deleteSubscriptionOptionApi({dispatch, id});

    if ((subscriptionOptions.length === 2 && isOneTimeEnable) || (subscriptionOptions.length === 1 && !isOneTimeEnable)) {
      const updatedProduct = {
        custom_attributes: editable_store_product.custom_attributes.map((a) => {
          if (a.attribute_code === 'subscription_allow_onetime' || a.attribute_code === 'subscription_active') {
            return {...a, value: '0'};
          }

          return a;
        })
      };
      await deleteOneTimePurchaseOption();
      dispatch(editUserStoreProduct(updatedProduct));
      await saveStoreProductSubscriptionAttrApi({
        dispatch,
        product: {
          ...editable_store_product,
          ...updatedProduct
        },
        sku: editable_store_product.sku,
        attributeCode: 'subscription_active'
      });
    }
  };

  const handleSaveSubscriptionLabel = () => {
    const product = {
      sku,
      id,
      custom_attributes: [
        {
          attribute_code: 'subscription_label',
          value: subscriptionLabel
        }
      ]
    };
    saveStoreProductSubscriptionAttrApi({
      dispatch,
      product,
      sku,
      attributeCode: 'subscription_label'
    });
  };

  return (
    <Grid container spacing={3} alignItems="center">
      <Grid item xs={3}>Enable:</Grid>
      <Grid item xs={9}>
        <Switch
          color="primary"
          name="subscription_active"
          disabled={isDisableWhileSaving}
          checked={editable_store_product.custom_attributes?.find((a) => a.attribute_code === 'subscription_active')?.value == 1}
          onClick={(event) => {
            event.stopPropagation();
            handleEnableSubscription(event);
          }}
        />
        <ConfirmDialog
          title="Do you want to enable Subscription?"
          open={openConfirmDialog}
          setOpen={setOpenConfirmDialog}
          onConfirm={() => {
            setOpenConfirmDialog(false);
            setOpenAddingSubscriptionsDialog(true);
          }}
        >
          Firstly add a subscription options to enable subscription for this product
        </ConfirmDialog>
      </Grid>
      <Grid item xs={3}>Allow One Time Purchase:</Grid>
      <Grid item xs={9}>
        <Switch
          color="primary"
          name="subscription_allow_onetime"
          disabled={isDisableWhileSaving}
          checked={editable_store_product.custom_attributes?.find((a) => a.attribute_code === 'subscription_allow_onetime')?.value == 1}
          onChange={handleAllowOneTimePurchase}
        />
      </Grid>
      <Grid item xs={3}>Subscription Label:</Grid>
      <Grid item xs={9}>
        <Typography variant="body1" className={updateTitle ? null : 'pt-0'}>
          {updateTitle ?
            <div className="flex items-center">
              <TextField
                variant="outlined"
                name="name"
                className="w-full max-w-300"
                value={subscriptionLabel}
                onChange={(e) => setSubscriptionLabel(e.target.value)}
              />
              <Button
                variant="contained"
                className="ml-2"
                onClick={() => {
                  setSubscriptionLabel(initialSubscriptionLabel);
                  setUpdatedTitle(false);
                }}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                className="ml-2"
                onClick={() => {
                  handleSaveSubscriptionLabel();
                  setUpdatedTitle(false);
                }}
              >
                Save
              </Button>
            </div>
            :
            <div className="flex items-center">
              {savingProductSubscriptionAttr ?
                <CircularProgress />
                :
                <>
                  {subscriptionLabel || 'Subscription Type'}
                  <IconButton
                    title="Edit Product Title"
                    className="ml-2"
                    onClick={() => {
                      setUpdatedTitle(true);
                    }}
                  >
                    <Icon>edit</Icon>
                  </IconButton>
                </>
              }
            </div>
          }
        </Typography>
      </Grid>
      <Grid item xs={12}>Subscription Options:</Grid>
      {(addSubscriptionOptionLoading || deleteOneTimeSubscriptionOptionLoading) && <CircularProgress className="ml-2" />}
      {!addSubscriptionOptionLoading && !deleteOneTimeSubscriptionOptionLoading && subscriptionOptions.length !== 0 &&
        <MUIDataTable
          className="m-3"
          data={subscriptionOptions}
          columns={columns}
          options={{
            filter: false,
            selectableRows: false,
            download: false,
            print: false,
            search: false,
            pagination: false,
            viewColumns: false,
            sortOrder: {
              name: 'ssort_order',
              direction: 'asc'
            }
          }}
        />
      }
      <Grid item xs={12}>
        {(!isOneTimeEnable && freequencyOptions.length !== existFrequency.length) || (isOneTimeEnable && freequencyOptions.length !== (existFrequency.length - 1)) ?
          <Button
            variant="contained"
            className="bg-light-primary mb-4"
            onClick={() => setOpenAddingSubscriptionsDialog(true)}
          >
            Add Subscription Options
          </Button>
          : <Typography variant="body1" className="mb-4 ml-2 text-muted">All available options already created you can delete or edit them.</Typography>
        }
        <Dialog
          className={classes.dialog}
          open={openAddingSubscriptionsDialog}
          onClose={handleCloseAddingSubscriptionsDialog}
          aria-labelledby="form-dialog-title"
        >
          <DialogTitle id="form-dialog-title">Subscription Options:</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Choose options for subscription
            </DialogContentText>
            <Formik
              initialValues={{
                subscriptions: [
                  {
                    product_id: id,
                    frequency_count: '',
                    frequency_unit: '',
                    sproduct_label: '',
                    installment_price: '',
                    additional_information: '',
                    ssort_order: '',
                  }
                ]
              }}
              enableReinitialize
              validationSchema={
                yup.object({
                  subscriptions: yup
                    .array().of(
                      yup.object().shape({
                        frequency: yup
                          .string()
                          .required('Field is required'),
                        percent_discount: yup
                          .string()
                          .test(
                            'percent_discount',
                            'Allowed range from 1 to 100',
                            (val) => {
                              const percentValue = val ? Number(val.replace(/%/g, '')) : 0;

                              if (!val) {
                                return true;
                              }

                              return percentValue <= 100;
                            }
                          )
                          .required('Field is required'),
                      })
                    )
                })
              }
              onSubmit={(values) => {
                values.subscriptions.map((item) => {
                  Reflect.deleteProperty(item, 'percent_discount');
                  Reflect.deleteProperty(item, 'frequency');
                });

                handleSaveSubscriptionOption(values);
              }}
            >
              {({
                values,
                errors,
                isValid,
                dirty,
                handleBlur,
                handleChange,
                handleSubmit,
                setFieldValue,
                setFieldTouched
              }) => (
                <form onSubmit={handleSubmit}>
                  <FieldArray name="subscriptions">
                    {({push, remove}) => {
                      const listOfUsedOptions = [...existFrequency, ...values.subscriptions.map((option) => option.frequency)];
                      return (
                        <>
                          <Grid item xs={12}>
                            {values.subscriptions.map((_, index) => (
                              <>
                                <CardContent key={`${name}-${index}`} className="px-0 flex items-start">
                                  <TextField
                                    className="mx-2 min-w-200"
                                    name={`subscriptions[${index}].frequency`}
                                    select={(!isOneTimeEnable && freequencyOptions.length !== existFrequency.length) || (isOneTimeEnable && freequencyOptions.length !== (existFrequency.length - 1))}
                                    disabled={(!isOneTimeEnable && freequencyOptions.length === existFrequency.length) || (isOneTimeEnable && freequencyOptions.length === (existFrequency.length - 1))}
                                    label="Frequency"
                                    value={values.frequency_count}
                                    variant="outlined"
                                    onChange={(event) => {
                                      const {value} = event.target;
                                      const ssort_order = value.includes('month') ? `${value.split(' ')[0]}0` : `${value.split(' ')[0]}00`;

                                      setFieldValue(`subscriptions[${index}].sproduct_label`, `Every ${value.replace('1 ', '')}`);
                                      setFieldValue(`subscriptions[${index}].ssort_order`, ssort_order);
                                      setFieldValue(`subscriptions[${index}].frequency_unit`, value.replace('s', '').split(' ')[1]);
                                      setFieldValue(`subscriptions[${index}].frequency_count`, value.split(' ')[0]);
                                      handleChange(event);
                                    }}
                                    onBlur={(event) => {
                                      const {value} = event.target;
                                      const ssort_order = value?.includes('month') ? `${value.split(' ')[0]}0` : `${value.split(' ')[0]}00`;

                                      setFieldValue(`subscriptions[${index}].sproduct_label`, `Every ${value.replace('1 ', '')}`);
                                      setFieldValue(`subscriptions[${index}].ssort_order`, ssort_order);
                                      setFieldValue(`subscriptions[${index}].frequency_unit`, value.replace('s', '').split(' ')[1]);
                                      setFieldValue(`subscriptions[${index}].frequency_count`, value.split(' ')[0]);
                                      handleBlur(event);
                                    }}
                                    inputProps={{ className: 'text-center' }}
                                    error={errors.subscriptions?.length && Boolean(errors.subscriptions[index]?.frequency)}
                                    helperText={errors.subscriptions?.length && errors.subscriptions[index]?.frequency}
                                  >
                                    {freequencyOptions.map((option, idx) => (
                                      <MenuItem
                                        key={`option-${idx}`}
                                        value={option}
                                        className={`${listOfUsedOptions.includes(option) ? 'hidden' : ''}`}
                                      >
                                        {option}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                  <NumberFormat
                                    customInput={TextField}
                                    className="mx-2 min-w-200"
                                    name={`subscriptions[${index}].percent_discount`}
                                    allowNegative={false}
                                    decimalScale={2}
                                    label="Percent discount"
                                    variant="outlined"
                                    suffix="%"
                                    value={values.percent_discount}
                                    onChange={(event) => {
                                      const {value = ''} = event.target;

                                      if (value) {
                                        setFieldValue(`subscriptions[${index}].installment_price`, (editable_store_product.price - ((editable_store_product.price / 100) * value.slice(0, -1))));
                                        setFieldValue(`subscriptions[${index}].additional_information`, [{discount_percentage: parseFloat(value)}]);
                                      }

                                      handleChange(event);
                                    }}
                                    onBlur={() => setFieldTouched(`subscriptions[${index}].percent_discount`, true)}
                                    inputProps={{ className: 'text-center' }}
                                    error={errors.subscriptions?.length && Boolean(errors.subscriptions[index]?.percent_discount)}
                                    helperText={errors.subscriptions?.length && errors.subscriptions[index]?.percent_discount}
                                  />
                                  {values.subscriptions.length > 1 &&
                                    <div>
                                      <IconButton
                                        onClick={() => remove(index)}
                                        title="Delete"
                                      >
                                        <Icon>delete</Icon>
                                      </IconButton>
                                    </div>
                                  }
                                </CardContent>
                                <Grid item xs={12}>
                                  {(values.subscriptions.length !== (index + 1)) && <Divider className="my-5" />}
                                </Grid>
                              </>
                            ))}
                            <DialogActions className="m-4">
                              <Button
                                variant="outlined"
                                color="primary"
                                disabled={!(isValid && dirty) || listOfUsedOptions.length === freequencyOptions.length}
                                onClick={() => push(emptySubscription)}
                              >
                                <span className="capitalize">Add more subscriptions</span>
                              </Button>
                              <Button
                                className="text-muted"
                                onClick={handleCloseAddingSubscriptionsDialog}
                              >
                                Cancel
                              </Button>
                              <Button
                                type="submit"
                                disabled={!(isValid && dirty)}
                                color="primary"
                                variant="contained"
                              >
                                Create Option
                              </Button>
                            </DialogActions>
                          </Grid>
                        </>
                      );
                    }}
                  </FieldArray>
                </form>
              )}
            </Formik>
          </DialogContent>
        </Dialog>
        <Dialog
          className={classes.dialog}
          open={Boolean(openEditDialog)}
          onClose={handleCloseAddingSubscriptionsDialog}
          aria-labelledby="form-dialog-title"
        >
          <DialogTitle id="form-dialog-title">Subscription Options:</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Edit options for subscription
            </DialogContentText>
            <Formik
              initialValues={{
                editedSubscriptionOption: {
                  product_id: id,
                  frequency_count: editableSubscirption?.frequency_count,
                  frequency_unit: editableSubscirption?.frequency_unit,
                  sproduct_label: editableSubscirption?.sproduct_label,
                  installment_price: editableSubscirption?.installment_price,
                  additional_information: editableSubscirption?.additional_information,
                  ssort_order: editableSubscirption?.ssort_order,
                },
                frequency: editableSubscirption
                  ?
                  `${editableSubscirption.frequency_count} ${editableSubscirption.frequency_unit}${editableSubscirption.frequency_count > 1 ? 's' : ''}`
                  : '',
                percent_discount: editableSubscirption
                  ? `${editableSubscirption.additional_information.length ? JSON.parse(`${editableSubscirption.additional_information[0]}`).discount_percentage : ''}${editableSubscirption.frequency_count != 0 ? '%' : ''}`
                  : '',
              }}
              enableReinitialize
              validationSchema={
                yup.object({
                  frequency: yup
                    .string()
                    .required('Required'),
                  percent_discount: yup
                    .string()
                    .test(
                      'percent_discount',
                      'Allowed range from 1 to 100',
                      (val) => {
                        const percentValue = val ? Number(val.replace(/%/g, '')) : 0;

                        if (!val) {
                          return true;
                        }

                        return percentValue <= 100;
                      }
                    )
                    .required('Field is Required')
                })
              }
              onSubmit={handleSaveSubscriptionOption}
            >
              {({
                values,
                errors,
                touched,
                isValid,
                dirty,
                setFieldValue,
                handleBlur,
                handleChange,
                handleSubmit,
                setFieldTouched
              }) => (
                <form onSubmit={handleSubmit}>
                  <Grid item xs={12}>
                    <CardContent className="px-0">
                      <TextField
                        className="mx-2 min-w-200"
                        name="frequency"
                        select
                        label={'Frequency'}
                        value={values.frequency}
                        variant="outlined"

                        onChange={(event) => {
                          const {value} = event.target;
                          const ssort_order = value.includes('month') ? `${value.split(' ')[0]}0` : `${value.split(' ')[0]}00`;

                          setFieldValue('editedSubscriptionOption.sproduct_label', `Every ${value.replace('1 ', '')}`);
                          setFieldValue('editedSubscriptionOption.ssort_order', ssort_order);
                          setFieldValue('editedSubscriptionOption.frequency_unit', value.replace('s', '').split(' ')[1]);
                          setFieldValue('editedSubscriptionOption.frequency_count', value.split(' ')[0]);
                          setFieldValue('editedSubscriptionOption.frequency', value);

                          dispatch(editSubscriptionOption(values.editedSubscriptionOption, editable_store_product.id, editable_store_product.price, editableOptionId));
                          handleChange(event);
                        }}
                        onBlur={(event) => {
                          const {value} = event.target;
                          const ssort_order = value.includes('month') ? `${value.split(' ')[0]}0` : `${value.split(' ')[0]}00`;

                          setFieldValue('editedSubscriptionOption.sproduct_label', `Every ${value.replace('1 ', '')}`);
                          setFieldValue('editedSubscriptionOption.ssort_order', ssort_order);
                          setFieldValue('editedSubscriptionOption.frequency_unit', value.replace('s', '').split(' ')[1]);
                          setFieldValue('editedSubscriptionOption.frequency_count', value.split(' ')[0]);
                          setFieldValue('editedSubscriptionOption.frequency', value);


                          dispatch(editSubscriptionOption(values.editedSubscriptionOption, editable_store_product.id, editable_store_product.price, editableOptionId));
                          handleBlur(event);
                        }}
                        inputProps={{ className: 'text-center' }}
                        error={touched.frequency && Boolean(errors.frequency)}
                        helperText={touched.frequency && errors.frequency}
                      >
                        {freequencyOptions.map((option, idx) => (
                          <MenuItem
                            key={`option-${idx}`}
                            value={option}
                            className={`${(existFrequency.includes(option) &&
                                        (`${editableSubscirption?.frequency_count} ${editableSubscirption?.frequency_unit}${editableSubscirption?.frequency_count > 1 ? 's' : ''}`) !== option)
                              ? 'hidden' : ''}`}
                          >
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>

                      <NumberFormat
                        customInput={TextField}
                        className="mx-2 min-w-200"
                        name="percent_discount"
                        allowNegative={false}
                        decimalScale={2}
                        suffix="%"
                        label={'Percent discount'}
                        variant="outlined"
                        value={values.percent_discount}
                        onChange={(event) => {
                          const {value} = event.target;

                          if (value) {
                            setFieldValue('editedSubscriptionOption.installment_price', (editable_store_product.price - ((editable_store_product.price / 100) * value.slice(0, -1))));
                            setFieldValue('editedSubscriptionOption.additional_information', [{discount_percentage: parseFloat(value)}]);

                            dispatch(editSubscriptionOption(values.editedSubscriptionOption, editable_store_product.id, editable_store_product.price, editableOptionId));
                          }

                          handleChange(event);
                        }}
                        onBlur={() => {
                          setFieldTouched('editedSubscriptionOption.percent_discount', true);
                          dispatch(editSubscriptionOption(values.editedSubscriptionOption, editable_store_product.id, editable_store_product.price, editableOptionId));
                        }}
                        inputProps={{ className: 'text-center' }}
                        error={touched?.editedSubscriptionOption?.percent_discount && Boolean(errors?.percent_discount)}
                        helperText={touched?.editedSubscriptionOption?.percent_discount && errors?.percent_discount}
                      />
                    </CardContent>
                    <DialogActions className="m-4">
                      <Button
                        className="text-muted"
                        onClick={() => setOpenEditDialog(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={!(isValid && dirty)}
                        color="primary"
                        variant="contained"
                      >
                        Edit Option
                      </Button>
                    </DialogActions>
                  </Grid>
                </form>
              )}
            </Formik>
          </DialogContent>
        </Dialog>
      </Grid>
    </Grid>
  );
};


export default Subscription;
